import React, { useEffect, useState } from "react";
import { XIcon, KeyRound, LockIcon, Loader2 } from "lucide-react";
import { setDeliveryPartnerPassword } from "../services/profile.service";
import { useToast } from "../contexts/ToastContext";

interface PasswordResetInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * User.id from GET /delivery/profile/ (`data.user.id`).
   * When set, shows self-service form calling POST …/admin-ops/staff/{id}/set-password/
   */
  staffUserId?: number | null;
}

const MIN_LEN = 8;

const PasswordResetInfoModal: React.FC<PasswordResetInfoModalProps> = ({
  isOpen,
  onClose,
  staffUserId,
}) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { showSuccess, showError } = useToast();

  const canSelfServe =
    typeof staffUserId === "number" && Number.isFinite(staffUserId) && staffUserId > 0;

  useEffect(() => {
    if (!isOpen) {
      setPassword("");
      setConfirm("");
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSelfServe) return;

    if (password.length < MIN_LEN) {
      showError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      showError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await setDeliveryPartnerPassword(staffUserId, password);
      showSuccess("Password updated successfully. Use your new password next time you sign in.");
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to update password.";
      showError(msg, 6000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex justify-center items-center p-4"
      onClick={() => {
        if (!submitting) onClose();
      }}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-password-title"
      >
        <div className="flex justify-between items-center p-4 border-b border-amber-200">
          <div className="flex items-center gap-2 min-w-0">
            <KeyRound size={22} className="text-orange-600 flex-shrink-0" />
            <h3
              id="reset-password-title"
              className="text-lg font-medium text-orange-800 truncate"
            >
              Reset password
            </h3>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!submitting) onClose();
            }}
            className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-amber-50 disabled:opacity-50"
            aria-label="Close"
            disabled={submitting}
          >
            <XIcon size={20} />
          </button>
        </div>

        {canSelfServe ? (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <p className="text-sm text-gray-600 leading-relaxed">
              Choose a new password (at least {MIN_LEN} characters).
            </p>
            <div>
              <label className="block text-sm font-medium text-orange-800 mb-1">
                New password
              </label>
              <div className="relative">
                <LockIcon
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400 pointer-events-none"
                />
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder={`At least ${MIN_LEN} characters`}
                  disabled={submitting}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-orange-800 mb-1">
                Confirm new password
              </label>
              <div className="relative">
                <LockIcon
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400 pointer-events-none"
                />
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Re-enter password"
                  disabled={submitting}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  if (!submitting) onClose();
                }}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium disabled:opacity-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : (
                  "Save password"
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-4 space-y-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              Sign in to the delivery app first. Then open the menu (☰) and use{" "}
              <strong>Reset password</strong> to set a new password from your profile.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordResetInfoModal;
